import React, { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  AdminTableToolbar,
  TableSearchInput,
  TableEmptyRow,
  ViewDetailsButton,
  TableActionsCell,
} from "../../common/adminTable";
import { list, search, markAsRead, getById } from "./Service";
import useTableSearch from "../../../hooks/useTableSearch";
import { useCloseOnSidebarNavigate } from "../../../hooks/useCloseOnSidebarNavigate";
import {
  formatListingDate as formatDate,
  formatListingDateTime as formatDateTime,
  formatListingTime as formatTime,
} from "../../../utils/formatListingDate";
import {
  BookingDetailHeader,
  BookingDetailSection,
  BookingDetailFields,
  BookingDetailMetaChip,
  BookingDetailBadge,
  BookingDetailNav,
  BookingDetailUserCard,
  InfoRow,
} from "../bookings/BookingDetailUi";
import { getPackageWebsiteUrl } from "../../../utils/websiteUrls";

const SECTION_IDS = {
  overview: "enquiry-section-overview",
  package: "enquiry-section-package",
  customer: "enquiry-section-customer",
  supplier: "enquiry-section-supplier",
};

const NAV_ITEMS = [
  { id: SECTION_IDS.overview, label: "Overview", icon: "fi fi-rr-apps" },
  { id: SECTION_IDS.package, label: "Package", icon: "fi fi-rr-backpack" },
  { id: SECTION_IDS.customer, label: "Customer", icon: "fi fi-rr-user" },
  { id: SECTION_IDS.supplier, label: "Supplier", icon: "fi fi-rr-building" },
];

const enquiryStatusClass = (isRead) =>
  isRead
    ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
    : "bg-blue-50 text-blue-700 ring-blue-600/20";

const Enquiry = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
    useTableSearch();
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedEnquiryId, setSelectedEnquiryId] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [activeSection, setActiveSection] = useState(SECTION_IDS.overview);
  const [copyToast, setCopyToast] = useState(null);

  const { data: enquiriesData } = useQuery({
    queryKey: ["enquiries", currentPage, activeSearch],
    queryFn: () =>
      activeSearch
        ? search({ search: activeSearch, page: currentPage })
        : list({ page: currentPage }),
    keepPreviousData: true,
  });

  const { data: selectedEnquiryData, isLoading: isLoadingDetails } = useQuery({
    queryKey: ["enquiry", selectedEnquiryId],
    queryFn: () => getById(selectedEnquiryId),
    enabled: !!selectedEnquiryId,
  });

  const markAsReadMutation = useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["enquiries"] });
      queryClient.invalidateQueries({ queryKey: ["enquiry", selectedEnquiryId] });
      showSuccessMessage("Enquiry marked as read");
    },
    onError: (error) => {
      showErrorMessage(error.message || "Failed to mark enquiry as read");
    },
  });

  const enquiries = enquiriesData?.data?.data || [];
  const selectedEnquiry = selectedEnquiryData?.data;
  const pagination = {
    currentPage: enquiriesData?.data?.current_page || 1,
    lastPage: enquiriesData?.data?.last_page || 1,
    perPage: enquiriesData?.data?.per_page || 10,
    total: enquiriesData?.data?.total || 0,
    from: enquiriesData?.data?.from || 0,
    to: enquiriesData?.data?.to || 0,
  };

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage("");
    }, 3000);
  };

  const showErrorMessage = () => {
    setShowError(true);
    setTimeout(() => setShowError(false), 3000);
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  const handleCloseDialog = useCallback(() => {
    setShowDetailsDialog(false);
    setSelectedEnquiryId(null);
    setActiveSection(SECTION_IDS.overview);
  }, []);

  useCloseOnSidebarNavigate(handleCloseDialog);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleViewDetails = (enquiry) => {
    setSelectedEnquiryId(enquiry.id);
    setActiveSection(SECTION_IDS.overview);
    setShowDetailsDialog(true);
  };

  const handleMarkAsRead = () => {
    if (selectedEnquiryId && !selectedEnquiry?.is_read) {
      markAsReadMutation.mutate(selectedEnquiryId, {
        onSuccess: () => handleCloseDialog(),
      });
    }
  };

  const handleCopied = useCallback((label, type = "success") => {
    setCopyToast({ message: `${label} copied to clipboard`, type });
    setTimeout(() => setCopyToast(null), 2500);
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    setActiveSection(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const pkg = selectedEnquiry?.package;
  const supplier = pkg?.user;
  const supplierDetails = supplier?.supplier_details;
  const packageWebsiteUrl = pkg
    ? getPackageWebsiteUrl(pkg.id, selectedEnquiry?.enquiry_date)
    : null;

  return (
    <AdminPageLayout>
      {copyToast && (
        <Toster
          message={copyToast.message}
          type={copyToast.type}
          duration={2500}
          onClose={() => setCopyToast(null)}
        />
      )}

      {showError && (
        <Toster
          message="An error occurred"
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}

      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      <AdminPageHeader
        title={ADMIN_PAGE_META.enquiry.title}
        subtitle={ADMIN_PAGE_META.enquiry.subtitle}
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <AdminTableToolbar className="mb-0">
            <TableSearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, phone..."
            />
          </AdminTableToolbar>
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            <AdminTableFooter>
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                showInfo={true}
              />
            </AdminTableFooter>
          }
        >
        <AdminTableHead>
          <AdminTableTh>#</AdminTableTh>
          <AdminTableTh>Name</AdminTableTh>
          <AdminTableTh>Email</AdminTableTh>
          <AdminTableTh>Phone</AdminTableTh>
          <AdminTableTh>Received Date</AdminTableTh>
          <AdminTableTh>Status</AdminTableTh>
          <AdminTableTh align="right">Actions</AdminTableTh>
        </AdminTableHead>
        <AdminTableBody>
          {enquiries.length > 0 ? (
            enquiries.map((enquiry, index) => (
              <AdminTableRow
                key={enquiry.id}
                className={!enquiry.is_read ? "bg-blue-50/50" : ""}
              >
                <AdminTableCell>{index + 1}</AdminTableCell>
                <AdminTableCell>{enquiry.name}</AdminTableCell>
                <AdminTableCell>{enquiry.email}</AdminTableCell>
                <AdminTableCell>{enquiry.phone}</AdminTableCell>
                <AdminTableCell>{formatDateTime(enquiry.created_at)}</AdminTableCell>
                <AdminTableCell>
                  <BookingDetailBadge className={enquiryStatusClass(enquiry.is_read)}>
                    {enquiry.is_read ? "Read" : "Unread"}
                  </BookingDetailBadge>
                </AdminTableCell>
                <TableActionsCell>
                  <ViewDetailsButton onClick={() => handleViewDetails(enquiry)} />
                </TableActionsCell>
              </AdminTableRow>
            ))
          ) : (
            <TableEmptyRow
              colSpan={7}
              searchQuery={activeSearch}
              emptyMessage="No enquiries found"
              searchMessage="No enquiries found matching your search"
              onClearSearch={clearSearch}
            />
          )}
        </AdminTableBody>
      </AdminTable>
      </AdminPageCard>

      <Dialog
        title="Enquiry Details"
        open={showDetailsDialog}
        onClose={handleCloseDialog}
        width="w-full"
        variant="detail"
        showCloseButton={false}
        primaryButtonText={selectedEnquiry?.is_read ? undefined : "Mark as Read"}
        primaryButtonAction={selectedEnquiry?.is_read ? undefined : handleMarkAsRead}
        primaryButtonClassName="bg-primary"
      >
        {!showDetailsDialog || isLoadingDetails || !selectedEnquiry ? (
          <div className="flex justify-center items-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
            <div className="space-y-5">
              <BookingDetailNav
                items={NAV_ITEMS}
                activeId={activeSection}
                onSelect={scrollToSection}
              />

              <BookingDetailHeader
                bookingId={selectedEnquiry.id}
                idLabel="Enquiry"
                bookedOnLabel="Received on"
                title={pkg?.name || "Package Enquiry"}
                onCopyBookingId={handleCopied}
                meta={
                  <>
                    <BookingDetailMetaChip icon="fi fi-rr-calendar">
                      Travel: {formatDate(selectedEnquiry.enquiry_date)}
                    </BookingDetailMetaChip>
                    {pkg?.starting_location ? (
                      <BookingDetailMetaChip icon="fi fi-rr-marker">
                        {pkg.starting_location}
                      </BookingDetailMetaChip>
                    ) : null}
                    {pkg ? (
                      <BookingDetailMetaChip icon="fi fi-rr-backpack">
                        {pkg.total_days || 0}D / {pkg.total_nights || 0}N
                      </BookingDetailMetaChip>
                    ) : null}
                    {selectedEnquiry.stay_category?.title ? (
                      <BookingDetailMetaChip icon="fi fi-rr-bed">
                        {selectedEnquiry.stay_category.title}
                      </BookingDetailMetaChip>
                    ) : null}
                  </>
                }
                status={selectedEnquiry.is_read ? "Read" : "Unread"}
                statusClassName={enquiryStatusClass(selectedEnquiry.is_read)}
                bookedAt={formatDate(selectedEnquiry.created_at)}
                bookedTime={formatTime(selectedEnquiry.created_at)}
                stats={[
                  { label: "Adults", value: selectedEnquiry.adult, icon: "fi fi-rr-user" },
                  { label: "Children", value: selectedEnquiry.child, icon: "fi fi-rr-users-alt" },
                  { label: "Infants", value: selectedEnquiry.infant, icon: "fi fi-rr-heart" },
                  {
                    label: "Status",
                    value: selectedEnquiry.is_read ? "Read" : "New",
                    highlight: !selectedEnquiry.is_read,
                  },
                ]}
              />

              <div id={SECTION_IDS.overview} className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <BookingDetailSection title="Enquiry Information" icon="fi fi-rr-document">
                  <BookingDetailFields
                    onCopied={handleCopied}
                    fields={[
                      {
                        label: "Enquiry ID",
                        value: `#${selectedEnquiry.id}`,
                        copyable: true,
                        copyValue: selectedEnquiry.id,
                      },
                      {
                        label: "Received On",
                        value: formatDateTime(selectedEnquiry.created_at),
                      },
                      {
                        label: "Travel Date",
                        value: formatDate(selectedEnquiry.enquiry_date),
                      },
                      {
                        label: "Stay Category",
                        value: selectedEnquiry.stay_category?.title || "—",
                      },
                      {
                        label: "Read At",
                        value: selectedEnquiry.read_at
                          ? formatDateTime(selectedEnquiry.read_at)
                          : "Not read yet",
                      },
                    ]}
                  />
                </BookingDetailSection>

                <BookingDetailSection title="Guest Count" icon="fi fi-rr-users">
                  <BookingDetailFields
                    fields={[
                      { label: "Adults", value: selectedEnquiry.adult },
                      { label: "Children", value: selectedEnquiry.child },
                      { label: "Infants", value: selectedEnquiry.infant },
                      {
                        label: "Total Guests",
                        value:
                          Number(selectedEnquiry.adult || 0) +
                          Number(selectedEnquiry.child || 0) +
                          Number(selectedEnquiry.infant || 0),
                        highlight: true,
                      },
                    ]}
                  />
                </BookingDetailSection>
              </div>

              <BookingDetailSection
                title="Additional Notes"
                icon="fi fi-rr-comment-alt"
                className="scroll-mt-20"
              >
                {selectedEnquiry.notes?.trim() ? (
                  <div className="rounded-lg border border-gray-100 bg-gray-50/60 px-4 py-3">
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">
                      {selectedEnquiry.notes.trim()}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No additional notes provided by the customer.</p>
                )}
              </BookingDetailSection>

              {pkg ? (
                <BookingDetailSection
                  id={SECTION_IDS.package}
                  title="Package Details"
                  icon="fi fi-rr-backpack"
                >
                  <BookingDetailFields
                    fields={[
                      { label: "Package Name", value: pkg.name, fullWidth: true, highlight: true },
                      {
                        label: "Tour Type",
                        value: pkg.tour_type?.replace(/_/g, " ") || "—",
                      },
                      {
                        label: "Duration",
                        value: `${pkg.total_days || 0} Days / ${pkg.total_nights || 0} Nights`,
                      },
                      {
                        label: "Group Size",
                        value: `Min ${pkg.minimum_members || 0} · Max ${pkg.maximum_members || 0}`,
                      },
                      {
                        label: "Starting Location",
                        value: pkg.starting_location || "—",
                        fullWidth: true,
                      },
                      { label: "Pickup Point", value: pkg.pickup_point || "—" },
                      { label: "Drop-off Point", value: pkg.dropoff_point || "—" },
                    ]}
                  />
                  {packageWebsiteUrl ? (
                    <a
                      href={packageWebsiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80"
                    >
                      View package on website
                      <i className="fi fi-rr-arrow-up-right-from-square text-xs" />
                    </a>
                  ) : null}
                </BookingDetailSection>
              ) : null}

              <BookingDetailSection
                id={SECTION_IDS.customer}
                title="Customer Details"
                icon="fi fi-rr-user"
              >
                <BookingDetailUserCard
                  user={{
                    name: selectedEnquiry.name,
                    email: selectedEnquiry.email,
                    phone: selectedEnquiry.phone,
                  }}
                  onCopied={handleCopied}
                  showMemberSince={false}
                />
              </BookingDetailSection>

              {supplier ? (
                <BookingDetailSection
                  id={SECTION_IDS.supplier}
                  title="Supplier Details"
                  icon="fi fi-rr-building"
                >
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <InfoRow
                      label="Company"
                      value={supplierDetails?.company_name || "—"}
                      icon="fi fi-rr-building"
                    />
                    <InfoRow label="Contact Person" value={supplier.name} icon="fi fi-rr-user" />
                    <InfoRow
                      label="Email"
                      value={supplier.email}
                      icon="fi fi-rr-envelope"
                      href={supplier.email ? `mailto:${supplier.email}` : undefined}
                      copyValue={supplier.email}
                      onCopied={handleCopied}
                    />
                    <InfoRow
                      label="Phone"
                      value={supplier.phone}
                      icon="fi fi-rr-phone-call"
                      href={supplier.phone ? `tel:${supplier.phone}` : undefined}
                      copyValue={supplier.phone}
                      onCopied={handleCopied}
                    />
                  </div>
                </BookingDetailSection>
              ) : null}
            </div>
        )}
      </Dialog>
    </AdminPageLayout>
  );
};

export default Enquiry;
